/**
 * Business Unit Provider
 * 
 * Provider component for BusinessUnitContext.
 * NO hook exports, NO context creation - just the provider.
 * IMPORTANT: Uses WorkspaceScopeContext directly (not hook) to avoid circular dependency.
 */

import React, { useState, useCallback, ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { WorkspaceScope } from '@/lib/workspaceScope';
import { getAccessibleWorkspaceIds } from '@/lib/workspaceUtils';
import { BusinessUnitContext, type BusinessUnit, type BusinessUnitContextValue } from './BusinessUnit.context';
// Import context directly instead of hook to avoid circular dependency during bundling
import { WorkspaceScopeContext } from './WorkspaceScope.context';

export function BusinessUnitProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Use context directly instead of hook to avoid circular dependency during bundling
  const workspaceScopeContext = React.useContext(WorkspaceScopeContext);
  const workspaces = workspaceScopeContext?.workspaces || [];
  const workspacesLoading = workspaceScopeContext?.isLoading || false;
  
  // Get accessible workspace IDs - always return array
  // Use utility function to avoid direct dependency on workspace structure
  const accessibleWorkspaceIds = useMemo(() => {
    if (workspacesLoading || !workspaces || workspaces.length === 0) {
      return [];
    }
    return getAccessibleWorkspaceIds(workspaces);
  }, [workspaces, workspacesLoading]);
  
  // Initialize active business unit from localStorage
  const [activeBusinessUnitId, setActiveBusinessUnitIdState] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem('dsos_active_business_unit_id');
      return stored || null;
    } catch {
      return null;
    }
  });

  // Fetch business units via RPC
  // CRITICAL: Always call useQuery (never conditionally) to prevent React #310
  // The queryFn will handle empty cases gracefully
  const { data: businessUnits = [], isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['business-units', user?.id],
    queryFn: async (): Promise<BusinessUnit[]> => {
      // If no user or workspaces still loading, return empty array
      if (!user?.id || workspacesLoading) {
        return [];
      }

      // If no accessible workspaces yet, return empty array
      if (accessibleWorkspaceIds.length === 0) {
        return [];
      }

      try {
        const { data, error } = await supabase.rpc('get_my_business_units');

        if (error) {
          console.error('[BusinessUnitProvider] RPC error:', error);
          return [];
        }

        if (!data || !Array.isArray(data)) {
          return [];
        }

        // Deduplicate by ID
        const seen = new Set<string>();
        const deduplicated = data.filter((bu: BusinessUnit) => {
          if (seen.has(bu.id)) return false;
          seen.add(bu.id);
          return true;
        });

        // Sort by name
        return deduplicated.sort((a, b) => a.name.localeCompare(b.name));
      } catch (err) {
        console.error('[BusinessUnitProvider] Unexpected error:', err);
        return [];
      }
    },
    // Only enable when user exists AND workspaces are loaded
    // Don't check accessibleWorkspaceIds.length here to avoid query re-enabling issues
    enabled: !!user?.id && !workspacesLoading,
    staleTime: 5 * 60 * 1000,
  });

  // Calculate scope - always return valid object
  // CRITICAL: Keep scope stable during loading to prevent React #310
  const scope = useMemo((): WorkspaceScope => {
    // If still loading workspaces, return stable empty scope
    // This prevents scope from changing when workspaces load
    if (workspacesLoading) {
      return { type: 'all', workspaceIds: [] };
    }

    // If no accessible workspaces yet, return empty scope
    if (accessibleWorkspaceIds.length === 0) {
      return { type: 'all', workspaceIds: [] };
    }

    // If "All" selected or no selection
    if (activeBusinessUnitId === null) {
      return { type: 'all', workspaceIds: accessibleWorkspaceIds };
    }

    // Find workspace_id for selected business unit
    const selectedBU = businessUnits.find(bu => bu.id === activeBusinessUnitId);
    if (selectedBU) {
      return { type: 'single', workspaceId: selectedBU.workspace_id };
    }

    // Fallback to "all"
    return { type: 'all', workspaceIds: accessibleWorkspaceIds };
  }, [activeBusinessUnitId, businessUnits, accessibleWorkspaceIds, workspacesLoading]);

  // Set active business unit
  const setActiveBusinessUnitId = useCallback((businessUnitId: string | null) => {
    setActiveBusinessUnitIdState(businessUnitId);
    try {
      if (businessUnitId) {
        localStorage.setItem('dsos_active_business_unit_id', businessUnitId);
      } else {
        localStorage.removeItem('dsos_active_business_unit_id');
      }
    } catch (err) {
      console.warn('[BusinessUnitProvider] Failed to save to localStorage:', err);
    }
  }, []);

  const error = queryError ? { message: queryError.message } : null;

  const contextValue: BusinessUnitContextValue = {
    businessUnits,
    activeBusinessUnitId,
    setActiveBusinessUnitId,
    isLoading: isLoading || workspacesLoading,
    error,
    refetchBusinessUnits: () => refetch(),
    scope,
    accessibleWorkspaceIds,
  };

  return (
    <BusinessUnitContext.Provider value={contextValue}>
      {children}
    </BusinessUnitContext.Provider>
  );
}

